
import { computed, defineComponent, onMounted, ref, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import DaIcon from '@/components/elements/daIcon/DaIcon.vue';
import TermTooltip from '@/components/elements/termTooltip/TermTooltip.vue';
import { _ROUTER_NAME } from '@/enums';
import { useStudent } from '@/features/student/useStudent';
import Multiselect from '@suadelabs/vue3-multiselect';
import { _GET_DATE_2 } from '@/helper/formatDate';
import { _MONTH_DIFF } from '@/helper/monthDiff';
import { useTutorial } from '@/features/tutorial/useTutorial';
import {
	TUTORIAL_VIRTUAL_STUDENT,
	isTutorialVirtualStudent,
} from '@/features/tutorial/virtualStudent';

export default defineComponent({
	name: 'StudentDetail',
	components: { DaIcon, Multiselect, TermTooltip },
	setup() {
		const id = ref('');
		const router = useRouter();
		const student: any = ref(null);
		const tab = ref({
			label: '',
			value: '',
		} as any);
		const tabs = ref([
			{
				label: 'Kế hoạch hiện tại',
				value: 'plans_report_detail__current',
				tourId: 'tour-tab-current-select',
				tourLabelId: 'tour-tab-current',
				onSelect: onSelectCurrentTab,
				tooltip:
					'Kế hoạch đang được dạy, bao gồm các mục tiêu có thể báo cáo kết quả buổi học trên app Luca Pro.',
			},
			{
				label: 'Kế hoạch dự kiến',
				value: 'plans_report_detail__future',
				tourId: 'tour-tab-future',
				tooltip:
					'Kế hoạch can thiệp của tháng tiếp theo, sau khi Kế hoạch hiện tại kết thúc. KHDK được tạo tự động sau khi hoàn thành báo cáo của tháng của kế hoạch hiện tại.',
			},
			{
				label: 'Lịch sử kế hoạch',
				value: 'plans_report_detail__history_plan',
				tourId: 'tour-tab-history',
				tooltip:
					'Hiển thị tất cả các kế hoạch mà học sinh đã từng học, cùng với chi tiết của các kế hoạch',
			},
			{
				label: 'Ngân hàng mục tiêu',
				value: 'plans_report_detail__goal_bank',
				tourId: 'tour-tab-goal-bank',
				onSelect: onSelectGoalBankTab,
				tooltip:
					'Nơi lưu trữ các mục tiêu đã được nhập liệu sẵn bởi Trị liệu viên để sử dụng cho việc làm Kế hoạch can thiệp cho mỗi bé.',
			},
			{
				label: 'Mục tiêu hoàn thành',
				value: 'plans_report_detail__goal_completed',
				tourId: 'tour-tab-completed',
				tooltip:
					'Mục tiêu hoàn thành là mục tiêu được Trị liệu viên chọn "Mục tiêu đã hoàn thành" khi thực hiện báo cáo tháng.',
			},
			{
				label: 'Mục tiêu tạm dừng',
				value: 'plans_report_detail__goal_stop',
				tourId: 'tour-tab-paused',
				tooltip:
					'Mục tiêu tạm dừng là mục tiêu được Trị liệu viên chọn "Tạm dừng mục tiêu" khi thực hiện báo cáo tháng.',
			},
			{
				label: 'Tất cả MT',
				value: 'plans_report_detail__all',
				tourId: 'tour-tab-all',
				tooltip:
					'Nơi hiển thị tất cả các mục tiêu của học sinh trong hệ thống Luca',
			},
		]);
		const route = useRoute();
		const queryData = route.query;
		id.value = route.params.id ? route.params.id + '' : '';
		const { getStudent } = useStudent();
		const { phase, activeTask, advanceSpotlight } = useTutorial();

		watch(route, () => {
			const name = route?.name;
			tab.value = tabs.value.find((item) => item.value == name);
		});

		watch(tab, (value: { value: string }) => {
			const query = route.query;
			if (value) {
				router.push({ name: value.value, query: { ...query } });
			}
		});

		onMounted(() => {
			const name = route?.name;
			tab.value = tabs.value.find((item) => item.value == name);
		});

		const oldPayload = ref({
			'x-cross': route.query.org_id,
		});

		const is_old_data = ref(route.query.is_old_data === 'true');

		async function fetchStudent() {
			if (isTutorialVirtualStudent(id.value)) {
				student.value = { ...TUTORIAL_VIRTUAL_STUDENT };
				return;
			}
			const res = await getStudent(
				id.value,
				is_old_data.value ? oldPayload.value : null
			);
			if (res?.data) {
				student.value = res?.data;
			}
		}

		function onSelectCurrentTab() {
			if (
				phase.value === 'tour' &&
				(activeTask.value?.key === 'edit_plan_with_ai' ||
					activeTask.value?.key === 'edit_plan')
			) {
				advanceSpotlight();
			}
		}

		function onSelectGoalBankTab() {
			if (
				phase.value === 'tour' &&
				activeTask.value?.key === 'edit_goal_library'
			) {
				advanceSpotlight();
			}
		}

		const dateData = computed(() => {
			const join_date = route.query.join_date;
			const left_date = route.query.left_date;
			if (!join_date) {
				return '';
			}
			return `${_GET_DATE_2(join_date)} - ${
				left_date ? _GET_DATE_2(left_date) : 'Hiện tại'
			} | Đã học ${_MONTH_DIFF(join_date, left_date)} tháng`;
		});

		async function viewReport() {
			const query = route.query;
			await router.push({
				path: `/student/${id.value}/old-data`,
				query: {
					...query,
				},
			});
		}

		function exit() {
			const new_id = route.query.new_id;
			const r = router.resolve({
				path: `/record/student/${new_id}/staff-list`,
			});
			window.location.assign(r.href);
		}

		function toDetail() {
			const query = route.query;
			const params = route.params;
			return router.push({
				path: `/record/student/${params?.id}/staff-list`,
				query: {
					...query,
				},
			});
		}

		fetchStudent();

		return {
			tab,
			tabs,
			student,
			routerName: computed(() => _ROUTER_NAME),
			queryData,
			dateData,
			is_old_data,
			viewReport,
			exit,
			toDetail,
			onSelectCurrentTab,
			onSelectGoalBankTab,
		};
	},
});
